import { useCallback, useRef, useState } from 'react';
import { documentService } from '../services/documentService';

/**
 * Minimal MediaRecorder wrapper for recording a single voice clip and turning it
 * into a persisted voice-segment descriptor (audio_url/gcs_path/duration/
 * mime_type/transcription) via the real /api/audio/transcribe endpoint.
 *
 * Used by the message edit flow (Edit Bug ticket) so a user can add/replace audio
 * on an already-sent message. The compose flow keeps its own (draft-aware)
 * recorder in MainOfficeView; this hook is the lean, self-contained variant for
 * one-off recordings where draft migration isn't needed.
 *
 * Returns segments in the SAME shape the backend stores in
 * message_metadata.voice_segments, so they round-trip through the edit endpoint.
 */
// `conversationId` is optional and purely for ENERGY ATTRIBUTION (#377): it
// rides along to /api/audio/transcribe so a clip recorded while EDITING a
// message bills its transcription to that message's conversation on the Energy
// Receipt. Defaulting to null keeps every existing caller byte-identical.
export function useVoiceRecorder(conversationId = null) {
    const [isRecording, setIsRecording] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [transcribing, setTranscribing] = useState(false);

    const recorderRef = useRef(null);
    const streamRef = useRef(null);
    const chunksRef = useRef([]);
    const startedAtRef = useRef(0);
    const timerRef = useRef(null);

    const cleanup = useCallback(() => {
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        recorderRef.current = null;
        chunksRef.current = [];
        setIsRecording(false);
        setElapsed(0);
    }, []);

    const start = useCallback(async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : 'audio/webm';
            const recorder = new MediaRecorder(stream, { mimeType });
            chunksRef.current = [];
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            streamRef.current = stream;
            recorderRef.current = recorder;
            recorder.start();
            startedAtRef.current = Date.now();
            setIsRecording(true);
            setElapsed(0);
            timerRef.current = setInterval(() => setElapsed((p) => p + 1), 1000);
            return true;
        } catch (err) {
            cleanup();
            return false;
        }
    }, [cleanup]);

    const cancel = useCallback(() => {
        const recorder = recorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
            try { recorder.stop(); } catch (_) { /* ignore */ }
        }
        cleanup();
    }, [cleanup]);

    // Stop recording, upload + transcribe, and return a persisted segment
    // descriptor — or null if nothing was captured / transcription failed.
    const stopAndTranscribe = useCallback(async () => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === 'inactive') { cleanup(); return null; }

        const duration = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
        const blob = await new Promise((resolve) => {
            recorder.onstop = () => resolve(new Blob(chunksRef.current, { type: recorder.mimeType }));
            try { recorder.stop(); } catch (_) { resolve(new Blob()); }
        });
        cleanup();
        if (!blob || blob.size === 0) return null;

        setTranscribing(true);
        try {
            const result = await documentService.uploadAndTranscribeAudio(
                blob, conversationId,
            );
            return {
                audio_url: documentService.getAudioStreamUrl(result.audio_url),
                gcs_path: result.gcs_path,
                duration,
                mime_type: result.mime_type || blob.type || 'audio/webm',
                transcription: result.transcription || '[inaudible]',
            };
        } catch (err) {
            return null;
        } finally {
            setTranscribing(false);
        }
        // `conversationId` is in the deps so a conversation switch can't leave
        // the closure attributing a clip to the previously-open thread.
    }, [cleanup, conversationId]);

    return { isRecording, elapsed, transcribing, start, cancel, stopAndTranscribe };
}

export default useVoiceRecorder;
